import { makeStyles } from "@mui/styles";
import { Alert } from "antd";
import { Button } from "components/Button/Button";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import NotificationService from "components/Notification/Notification.service";
import { Popover } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import { MoveToLocationAutocomplete } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationAutocomplete";
import { moveToLocationAction } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationForm";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import { WrappedResource } from "containers/Resources/components/CommonActions/interfaces";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { getRelativeUrl } from "utils/service";
import {
    checkContainerItemType,
    checkNonBundleItemType,
    checkNonProjectItemType,
    checkNonWorkspaceItemType,
    getRecordsLocationId,
} from "utils/utils";
import "./MoveToLocation.scss";

interface MoveToProjectProps {
    resourceListRaw: WrappedResource[];
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    afterAction?: () => void;
}

const getWarning = (resources: WrappedResource[], text: string) => {
    return (
        <div className="warningMessage">
            <Alert
                message={text}
                description={resources.map((resource) => (
                    <RecordLabel
                        key={resource.resourceData.id}
                        name={resource.resourceData.name}
                        link={getRelativeUrl(resource.resourceName, resource.resourceData.id)}
                        iconByType={resource.resourceName}
                        // manually override typings
                        // when the resource is a project or bundle, there may be an icon
                        // in all other cases its just undefined which is fine too
                        image={
                            resource.resourceName === "project" ||
                            resource.resourceName === "bundle"
                                ? (resource.resourceData as any)?.icon
                                : undefined
                        }
                    />
                ))}
                type="warning"
            />
        </div>
    );
};

// filter out resources that can't be moved
// order is important here. resources that have already been filtered out will not be caught again by the next filter.
export const classifyResources = (
    resourceList: WrappedResource[],
    selectedLocation?: AutocompleteOption
) => {
    const selectedLocationType = selectedLocation ? selectedLocation.type : undefined;

    const isNonLocationItem = ({ resourceName }) => {
        const resourceType = resourceName.toUpperCase();
        if (!selectedLocationType && !checkContainerItemType(resourceType)) {
            return false;
        }
        switch (selectedLocationType) {
            case "workspace": {
                return checkNonWorkspaceItemType(resourceType);
            }
            case "project": {
                return checkNonProjectItemType(resourceType);
            }
            case "bundle": {
                return checkNonBundleItemType(resourceType);
            }
            default:
                return true;
        }
    };

    const isReadOnlyItem = ({ resourceData }) =>
        resourceData.is_readonly === true ||
        resourceData.is_project_readonly === true ||
        resourceData.is_bundle_readonly === true;

    const isDeletedItem = ({ resourceData }) => resourceData.deleted_at !== null;

    // Get resources that can't be moved due to location incompatibility
    const nonLocationItemsToMove = resourceList.filter(isNonLocationItem);

    // Filter out non-location items to continue checking other blocking conditions
    let filterableResources = resourceList.filter((resource) => !isNonLocationItem(resource));

    // From location-compatible resources, get those that can't be moved due to read-only status
    const readOnlyToMove = filterableResources.filter(isReadOnlyItem);

    // Filter out read-only resources to continue checking remaining blocking conditions
    filterableResources = filterableResources.filter((resource) => !isReadOnlyItem(resource));

    // From remaining resources, get those that can't be moved due to being deleted
    const deletedToMove = filterableResources.filter(isDeletedItem);

    // Create list of resources that can actually be moved (pass all blocking condition checks)
    const moveableResources = resourceList.filter(
        (resource) =>
            !isNonLocationItem(resource) && !isReadOnlyItem(resource) && !isDeletedItem(resource)
    );

    return {
        nonLocationItemsToMove, // Can't move: wrong location type
        readOnlyToMove, // Can't move: read-only (but right location type)
        deletedToMove, // Can't move: deleted (but right location type and not read-only)
        moveableResources, // Can be moved (pass all checks)
    };
};

export const getSelectedLocationKey = (type: string) => `${type}_id`;

export const MoveResourcesToLocation = ({
    resourceListRaw: resourceList,
    isOpen,
    onConfirm,
    onCancel,
    afterAction,
}: MoveToProjectProps) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const recordsLocationId = getRecordsLocationId(resourceList);

    const { selectedLocation, changeValue, options, isSameLocation } = useMoveToLocation(
        undefined,
        recordsLocationId
    );

    const { nonLocationItemsToMove, readOnlyToMove, deletedToMove, moveableResources } =
        classifyResources(resourceList, selectedLocation);

    const moveResources = async (moveableResources: WrappedResource[]) => {
        if (selectedLocation) {
            await Promise.allSettled(
                moveableResources.map(({ resourceName, resourceData }) =>
                    moveToLocationAction({
                        dispatch,
                        resourceName,
                        id: resourceData.id,
                        selectedLocation,
                    })
                )
            )
                .then((results) => {
                    const successfullyMoved = results.filter(
                        (result) => result.status === "fulfilled"
                    );
                    const failedToMove = results.filter((result) => result.status === "rejected");

                    if (failedToMove.length > 0) {
                        NotificationService.error(
                            `Could not move ${failedToMove.length} resource${
                                failedToMove.length > 1 ? "s" : ""
                            } to ${selectedLocation.type} "${selectedLocation.label}"`
                        );
                    }

                    if (successfullyMoved.length > 0) {
                        NotificationService.info(
                            `Moved ${successfullyMoved.length} resource${
                                successfullyMoved.length > 1 ? "s" : ""
                            } to ${selectedLocation.type} "${selectedLocation.label}"`
                        );
                    }
                })
                .catch(console.error)
                .finally(() => {
                    afterAction && afterAction();
                });
        }
    };

    const rootStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    };
    const paperStyle = {
        maxHeight: "600px",
        overflow: "visible",
    };
    const useStyles = makeStyles(() => ({
        rootStyle,
        paperStyle,
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={isOpen}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="moveToLocation__popover"
            contentClassName="moveToLocation__content"
            anchorReference="none"
        >
            <div className="moveToLocation__header">
                Move {resourceList.length > 1 ? resourceList.length : ""} selected resource
                {resourceList.length > 1 ? "s" : ""} to:
            </div>
            <MoveToLocationAutocomplete
                name="location"
                changeValue={changeValue}
                value={selectedLocation?.value}
                options={options}
            />
            {nonLocationItemsToMove.length && selectedLocation
                ? getWarning(
                      nonLocationItemsToMove,
                      "These resources cannot be moved to the selected location"
                  )
                : null}
            {readOnlyToMove.length
                ? getWarning(readOnlyToMove, "Read-Only resources cannot be moved")
                : null}
            {deletedToMove.length
                ? getWarning(deletedToMove, "Resources in trash cannot be moved")
                : null}
            <div className="button-container">
                <CancelButton onClick={onCancel} />
                <Button
                    className="confirm-button"
                    variation="primary"
                    disabled={!moveableResources.length || !selectedLocation || isSameLocation}
                    loading={isLoading}
                    onClick={async () => {
                        setIsLoading(true);
                        await moveResources(moveableResources);
                        setIsLoading(false);
                        onConfirm();
                    }}
                    label={"Move"}
                />
            </div>
        </Popover>
    );
};
